import { html, nothing } from "lit";
import { resolveSubagentActivityPhase } from "../chat/subagent-run-status.ts";
import type { ChatSubagentCard } from "../ui-types.ts";

export type FleetPipelinePhase = "planning" | "execution" | "review" | "final";

export type FleetExecutionStatus =
  | "waiting"
  | "running"
  | "completed"
  | "blocked"
  | "failed"
  | "paused";

export type FleetExecutionFile = {
  path: string;
  change: "M" | "A" | "D";
};

export type FleetExecutionCheck = {
  label: string;
  status: "passed" | "running" | "pending" | "failed";
  detail?: string;
};

export type FleetExecutionAgentRow = {
  id: string;
  name: string;
  purpose: string;
  roleId: string;
  phase: FleetPipelinePhase;
  status: FleetExecutionStatus;
  done: number;
  total: number;
  durationLabel: string;
  logs: string[];
  files: FleetExecutionFile[];
  checks: FleetExecutionCheck[];
};

export type FleetExecutionResult = {
  title: string;
  summary: string;
  done: string[];
  files: FleetExecutionFile[];
  checks: FleetExecutionCheck[];
  unresolved: string[];
  nextSteps: string[];
  needsDecision: boolean;
};

export type FleetExecutionViewModel = {
  agentCount: number;
  taskCount: number;
  branch?: string;
  currentPhase: FleetPipelinePhase;
  agents: FleetExecutionAgentRow[];
  result: FleetExecutionResult | null;
};

export type FleetExecutionPipelineProps = {
  model: FleetExecutionViewModel;
  expandedId: string | null;
  onToggle: (id: string) => void;
};

const PHASES: Array<{ id: FleetPipelinePhase; label: string; step: number }> = [
  { id: "planning", label: "Planning", step: 1 },
  { id: "execution", label: "Execution", step: 2 },
  { id: "review", label: "Review", step: 3 },
  { id: "final", label: "Final result", step: 4 },
];

const PHASE_ORDER: FleetPipelinePhase[] = ["planning", "execution", "review", "final"];

const STATUS_LABEL: Record<FleetExecutionStatus, string> = {
  waiting: "Waiting",
  running: "Running",
  completed: "Completed",
  blocked: "Blocked",
  failed: "Failed",
  paused: "Paused",
};

export function renderFleetExecutionPipeline(props: FleetExecutionPipelineProps) {
  const { model } = props;
  const currentIndex = PHASE_ORDER.indexOf(model.currentPhase);
  return html`
    <div class="fleet-exec" data-testid="fleet-execution-pipeline">
      <header class="fleet-exec__meta">
        <span>${model.agentCount} agents</span>
        <span>${model.taskCount} tasks</span>
        ${model.branch ? html`<span class="mono">${model.branch}</span>` : nothing}
      </header>
      <ol class="fleet-exec__phases" aria-label="Fleet pipeline">
        ${PHASES.map((phase, index) => {
          const done =
            index < currentIndex || (index === currentIndex && model.currentPhase === "final");
          const current = index === currentIndex && model.currentPhase !== "final";
          const completeFinal = model.currentPhase === "final";
          return html`
            <li
              class="fleet-exec__phase ${done || completeFinal ? "is-done" : ""} ${current ? "is-current" : ""}"
              data-testid=${`fleet-exec-phase-${phase.id}`}
            >
              <span class="fleet-exec__phase-index">${phase.step}</span>
              <span>${phase.label}</span>
              ${done || completeFinal ? html`<span aria-hidden="true">✓</span>` : nothing}
            </li>
          `;
        })}
      </ol>
      <h2 class="fleet-exec__title">Fleet execution</h2>
      <div class="fleet-exec__agents">
        ${model.agents.map((agent) => renderAgentRow(agent, props.expandedId === agent.id, props.onToggle))}
      </div>
      ${model.result ? renderResult(model.result) : nothing}
    </div>
  `;
}

function renderAgentRow(
  agent: FleetExecutionAgentRow,
  expanded: boolean,
  onToggle: (id: string) => void,
) {
  const percent = agent.total > 0 ? Math.round((agent.done / agent.total) * 100) : 0;
  return html`
    <article class="fleet-exec__agent ${expanded ? "is-open" : ""}">
      <button
        type="button"
        class="fleet-exec__agent-head"
        aria-expanded=${expanded}
        @click=${() => onToggle(agent.id)}
      >
        <span class="fleet-exec__agent-name">${agent.name}</span>
        <span class="fleet-exec__agent-purpose">${agent.purpose}</span>
        <span class="fleet-exec__status fleet-exec__status--${agent.status}">
          ${STATUS_LABEL[agent.status]}
        </span>
        <span
          class="fleet-exec__bar"
          role="progressbar"
          aria-label=${`${agent.name} progress`}
          aria-valuemin="0"
          aria-valuemax=${agent.total}
          aria-valuenow=${agent.done}
        >
          <span class="fleet-exec__bar-fill" style=${`width:${percent}%`}></span>
        </span>
        <span class="fleet-exec__count">${agent.done}/${agent.total}</span>
        <span class="fleet-exec__time">${agent.durationLabel}</span>
      </button>
      ${
        expanded
          ? html`
              <div class="fleet-exec__agent-body">
                <section>
                  <h3>Implementation output</h3>
                  <pre class="fleet-exec__log">
${agent.logs.join("\n") || "No live output yet."}</pre>
                </section>
                <section>
                  <h3>Changed files (${agent.files.length})</h3>
                  <ul class="fleet-exec__files">
                    ${
                      agent.files.length === 0
                        ? html`<li class="muted">No files yet.</li>`
                        : agent.files.map(
                            (file) => html`
                              <li><span class="mono">${file.change}</span> ${file.path}</li>
                            `,
                          )
                    }
                  </ul>
                </section>
                <section>
                  <h3>Validation evidence</h3>
                  <ul class="fleet-exec__checks">
                    ${
                      agent.checks.length === 0
                        ? html`<li class="muted">No checks recorded yet.</li>`
                        : agent.checks.map(
                            (check) => html`
                              <li class="fleet-exec__check fleet-exec__check--${check.status}">
                                ${check.label}${check.detail ? html` · ${check.detail}` : nothing}
                              </li>
                            `,
                          )
                    }
                  </ul>
                </section>
              </div>
            `
          : nothing
      }
    </article>
  `;
}

function renderResult(result: FleetExecutionResult) {
  return html`
    <section class="card fleet-exec__result" data-testid="fleet-execution-result">
      <div class="fleet-exec__result-head">
        <div>
          <div class="card-title">${result.title}</div>
          <p>${result.summary}</p>
        </div>
        <span class="fleet-exec__status fleet-exec__status--completed">Completed</span>
      </div>
      <div class="fleet-exec__result-grid">
        <div>
          <h3>What was done</h3>
          <ul>
            ${result.done.map((item) => html`<li>${item}</li>`)}
          </ul>
        </div>
        <div>
          <h3>Validation evidence</h3>
          <ul>
            ${result.checks.map(
              (check) => html`
                <li class="fleet-exec__check fleet-exec__check--${check.status}">
                  ${check.label}${check.detail ? html` · ${check.detail}` : nothing}
                </li>
              `,
            )}
          </ul>
        </div>
        <div>
          <h3>Changed files (${result.files.length})</h3>
          <ul class="fleet-exec__files">
            ${result.files.map((file) => html`<li><span class="mono">${file.change}</span> ${file.path}</li>`)}
          </ul>
        </div>
        <div>
          <h3>Outstanding notes</h3>
          <ul>
            ${
              result.unresolved.length === 0
                ? html`<li class="muted">Nothing unresolved.</li>`
                : result.unresolved.map((item) => html`<li>${item}</li>`)
            }
          </ul>
          <h3>Next steps</h3>
          <ul>
            ${
              result.nextSteps.length === 0
                ? html`<li class="muted">No further steps.</li>`
                : result.nextSteps.map((item) => html`<li>${item}</li>`)
            }
          </ul>
          <p class="fleet-exec__decision">
            ${result.needsDecision ? "A decision is still needed." : "No further decision is needed."}
          </p>
        </div>
      </div>
    </section>
  `;
}

export function buildFleetExecutionView(input: {
  subagents: ChatSubagentCard[];
  branch?: string;
  todosDone?: number;
  todosTotal?: number;
  result?: FleetExecutionResult | null;
}): FleetExecutionViewModel {
  const agents = input.subagents.map((card) => cardToRow(card));
  const taskCount =
    typeof input.todosTotal === "number"
      ? input.todosTotal
      : agents.reduce((sum, agent) => sum + agent.total, 0);
  const currentPhase = derivePhase(agents);
  const allComplete = agents.length > 0 && agents.every((agent) => agent.status === "completed");
  return {
    agentCount: agents.length,
    taskCount,
    branch: input.branch,
    currentPhase: allComplete ? "final" : currentPhase,
    agents,
    result: allComplete
      ? (input.result ?? defaultResult(agents, input.branch))
      : (input.result ?? null),
  };
}

function cardToRow(card: ChatSubagentCard): FleetExecutionAgentRow {
  const phase = rolePhase(card.agentId, card.label, card.task);
  const status = cardStatus(card);
  const done = status === "completed" ? 1 : 0;
  const total = 1;
  const logs: string[] = [];
  if (card.streamText?.trim()) {
    logs.push(card.streamText.trim());
  }
  if (card.outputPreview?.trim()) {
    logs.push(card.outputPreview.trim());
  }
  if (card.events) {
    for (const event of card.events) {
      const line = [event.label, event.name, event.detail].filter(Boolean).join(" — ");
      if (line.trim()) {
        logs.push(line.trim());
      }
    }
  }
  return {
    id: card.id,
    name: card.label || card.agentId,
    purpose: card.task || card.title || "",
    roleId: card.agentId,
    phase,
    status,
    done,
    total,
    durationLabel: durationLabel(card.createdAt, card.updatedAt),
    logs,
    files: (card.changedPaths ?? []).map((path) => ({ path, change: "M" as const })),
    checks:
      status === "completed"
        ? [{ label: "Run completed", status: "passed" as const }]
        : status === "failed"
          ? [{ label: card.error || "Run failed", status: "failed" as const }]
          : [],
  };
}

function rolePhase(agentId: string, label: string, task: string): FleetPipelinePhase {
  const hay = `${agentId} ${label} ${task}`.toLowerCase();
  if (/\b(planner|plan|design)\b/.test(hay)) {
    return "planning";
  }
  if (/\b(test|review|qa)\b/.test(hay)) {
    return "review";
  }
  if (/\b(summar|coordinat|jasiu|jaisiu)\b/.test(hay)) {
    return "final";
  }
  return "execution";
}

function cardStatus(card: ChatSubagentCard): FleetExecutionStatus {
  const phase = resolveSubagentActivityPhase(card);
  if (phase === "ongoing") {
    return "running";
  }
  if (phase === "completed") {
    return "completed";
  }
  if (phase === "failed" || phase === "defunct") {
    return "failed";
  }
  if (phase === "blocked") {
    return "blocked";
  }
  if (phase === "cancelled") {
    return "paused";
  }
  return "waiting";
}

function derivePhase(agents: FleetExecutionAgentRow[]): FleetPipelinePhase {
  if (
    agents.some(
      (agent) =>
        agent.phase === "planning" && (agent.status === "running" || agent.status === "waiting"),
    )
  ) {
    return "planning";
  }
  if (agents.some((agent) => agent.phase === "execution" && agent.status === "running")) {
    return "execution";
  }
  if (
    agents.some(
      (agent) =>
        agent.phase === "review" && (agent.status === "running" || agent.status === "waiting"),
    )
  ) {
    return "review";
  }
  if (agents.length === 0) {
    return "planning";
  }
  if (agents.every((agent) => agent.status === "completed")) {
    return "final";
  }
  return "execution";
}

function durationLabel(createdAt: number, updatedAt?: number): string {
  const end = updatedAt && updatedAt > createdAt ? updatedAt : Date.now();
  const ms = Math.max(0, end - createdAt);
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) {
    return "<1m";
  }
  return `${minutes}m`;
}

function defaultResult(agents: FleetExecutionAgentRow[], branch?: string): FleetExecutionResult {
  const files = agents.flatMap((agent) => agent.files);
  const checks = agents.flatMap((agent) => agent.checks);
  return {
    title: branch ? `Done for ${branch}` : "Engagement complete",
    summary: "All required tasks completed. The result is available below.",
    done: agents.map((agent) => `${agent.name}: ${agent.purpose || "completed assigned work"}`),
    files,
    checks: checks.length > 0 ? checks : [{ label: "Fleet run completed", status: "passed" }],
    unresolved: [],
    nextSteps: [],
    needsDecision: false,
  };
}
